import { useState, useCallback } from 'react';
import {
  COUNTRY_CODES,
  validatePhoneNumber,
  formatPhoneDisplay,
  getPhoneMaxLength,
} from '@/utils/phone';
import { Phone } from 'lucide-react';

interface PhoneInputGroupProps {
  label?: string;
  value: string;
  onChange: (phone: string, isValid: boolean) => void;
  countryCode: string;
  onCountryCodeChange: (code: string) => void;
  error?: string;
  hint?: string;
  disabled?: boolean;
  required?: boolean;
  className?: string;
  placeholder?: string;
}

export function PhoneInputGroup({
  label,
  value,
  onChange,
  countryCode,
  onCountryCodeChange,
  error,
  hint,
  disabled = false,
  required = false,
  className = '',
  placeholder = '620 00 00 00',
}: PhoneInputGroupProps) {
  const [localError, setLocalError] = useState<string | null>(null);

  const digits = value.replace(/\D/g, '');
  const displayValue = formatPhoneDisplay(digits, countryCode);
  const maxLength = getPhoneMaxLength(countryCode);

  const handlePhoneChange = useCallback((raw: string) => {
    const cleaned = raw.replace(/\D/g, '').slice(0, maxLength);
    const isValid = cleaned.length === 0 || validatePhoneNumber(cleaned, countryCode);

    if (cleaned.length === maxLength && !validatePhoneNumber(cleaned, countryCode)) {
      setLocalError('Format de numéro invalide');
    } else {
      setLocalError(null);
    }

    onChange(cleaned, isValid);
  }, [countryCode, maxLength, onChange]);

  const handleCountryChange = (code: string) => {
    onCountryCodeChange(code);
    const isValid = digits.length === 0 || validatePhoneNumber(digits, code);
    if (digits.length > 0 && !isValid) {
      setLocalError('Format de numéro invalide');
    } else {
      setLocalError(null);
    }
    onChange(digits, isValid);
  };

  const displayError = error || localError;
  const selectedCountry = COUNTRY_CODES.find((c) => c.code === countryCode);

  return (
    <div className={className}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1.5">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}

      <div
        className={`
          flex w-full items-stretch rounded-lg border bg-white overflow-hidden
          transition-colors duration-200
          focus-within:ring-2 focus-within:ring-offset-0
          disabled:opacity-60
          ${displayError
            ? 'border-red-300 focus-within:border-red-500 focus-within:ring-red-500/20'
            : 'border-gray-300 focus-within:border-teal-500 focus-within:ring-teal-500/20'
          }
          ${disabled ? 'bg-gray-50 cursor-not-allowed' : ''}
        `}
      >
        {/* Indicatif pays — select natif, pas de dropdown flottant */}
        <div className="relative shrink-0 border-r border-gray-200 bg-gray-50/80">
          <select
            value={countryCode}
            disabled={disabled}
            onChange={(e) => handleCountryChange(e.target.value)}
            className="h-full min-h-[46px] pl-3 pr-8 py-2.5 bg-transparent text-sm font-medium text-gray-800 focus:outline-none appearance-none cursor-pointer disabled:cursor-not-allowed"
            aria-label="Indicatif pays"
          >
            {COUNTRY_CODES.map((c) => (
              <option key={c.code} value={c.code}>
                {c.flag} {c.code}
              </option>
            ))}
          </select>
          <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 text-xs">
            ▾
          </span>
        </div>

        {/* Numéro formaté */}
        <div className="relative flex-1 min-w-0">
          <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
          <input
            type="tel"
            inputMode="numeric"
            autoComplete="tel-national"
            value={displayValue}
            onChange={(e) => handlePhoneChange(e.target.value)}
            disabled={disabled}
            placeholder={placeholder}
            className="block w-full min-h-[46px] bg-transparent pl-10 pr-4 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none disabled:cursor-not-allowed"
          />
        </div>
      </div>

      {selectedCountry && !displayError && !hint && (
        <p className="mt-1 text-xs text-gray-400">
          {selectedCountry.country}
        </p>
      )}

      {displayError && (
        <p className="mt-1.5 text-sm text-red-600">{displayError}</p>
      )}
      {hint && !displayError && (
        <p className="mt-1.5 text-sm text-gray-500">{hint}</p>
      )}
    </div>
  );
}
